import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Col,
  Row,
  NMForm,
  Button,
  usePub,
  OutlinedButton,
} from "@neumetric/blueprint-lib/components";
import { useDispatch, useSelector } from "react-redux";
import {
  mitigatorActions,
  getAssetTypesList,
  getCurrentAsset,
  getCurrentAssetType,
} from "../../../store/";

const getAssetTypeFields = (isViewMode, isEditMode) => {
  const fields = [];
  if (isViewMode || isEditMode) {
    fields.push({
      label: "Asset Type ID",
      labelAlign: "top",
      attribute: "id",
      placeholder: "",
      readOnly: true,
      typeRepresentation: "TEXT",
      classes: { root: "w-100", label: "text-right w-101-px", input: "pl-12" },
    });
  }
  fields.push({
    label: "Asset Type",
    labelAlign: "top",
    attribute: "type",
    placeholder: "Asset Type",
    typeRepresentation: "TEXT",
    classes: { root: "w-100", label: "text-right w-101-px", input: "pl-12" },
  });
  return fields;
};

const getAssetFields = (isViewMode, isEditMode) => {
  const fields = [];
  if (isViewMode || isEditMode) {
    fields.push({
      label: "Asset ID",
      labelAlign: "top",
      attribute: "asset_id",
      placeholder: "",
      readOnly: true,
      typeRepresentation: "TEXT",
      classes: { root: "w-100", label: "text-right w-81-px", input: "pl-12" },
    });
  }
  fields.push(
    {
      label: "Asset Type",
      labelAlign: "top",
      attribute: "asset_type",
      placeholder: "Asset Type",
      typeRepresentation: isViewMode ? "TEXT" : "DROPDOWN",
      classes: {
        root: "w-100",
        label: "text-right w-91-px",
        input: "bg-contrast pl-12",
      },
    },
    {
      label: "Asset",
      labelAlign: "top",
      attribute: "asset",
      placeholder: "Asset",
      typeRepresentation: "TEXT",
      classes: { root: "w-100", label: "text-right w-91-px", input: "pl-12" },
    }
  );
  return fields;
};

export const CreateNewAssetType = () => {
  const dispatch = useDispatch();
  const publish = usePub();
  const [submitting, setSubmitting] = React.useState(false);
  const _ref = React.useRef(null);
  const viewRef = React.useRef(null);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const assetTypeId = params.get("id");
  const currentAssetType = useSelector(getCurrentAssetType);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (currentAssetType && viewRef.current) {
      viewRef.current.updateFormData({
        id: currentAssetType.asset_type_id,
        type: currentAssetType.asset_type,
      });
    }
  }, [currentAssetType]);

  useEffect(() => {
    if (!assetTypeId) return;
    dispatch(mitigatorActions.fetchAssetType({ asset_type_id: assetTypeId }));
  }, [assetTypeId]);

  const onComplete = () => {
    setSubmitting(false);
    setIsEditMode(false); // return to view mode
    publish("MITIGATOR_HIDE_NEW_CONTENT");
  };
  const onFailure = () => {
    setSubmitting(false);
  };
  const handleSave = () => {
    if (!_ref.current.validate()) {
      return;
    }
    const values = _ref.current.getValues();
    const body = {
      type: values.type,
    };
    // const action = mitigatorActions.createAssetType(body);
    const action = isEditMode
      ? mitigatorActions.updateAssetType({
        id: assetTypeId,
        ...body,
      })
      : mitigatorActions.createAssetType(body);
    action.onComplete = onComplete;
    action.onFailure = onFailure;
    setSubmitting(true);
    dispatch(action);
  };
  const handleCancel = () => {
    if (submitting) return;
    _ref.current?.reset?.();
    publish("MITIGATOR_HIDE_NEW_CONTENT"); //hides create panel
  };
  return (
    <div
      style={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        padding: "0 12px",
        boxSizing: "border-box",
      }}
    >
      {assetTypeId && currentAssetType && !isEditMode ? (
        <Col className="w-100 h-100 overflow-hide mt-36 pb-16">
          <Row justify="between" align="" className="w-100 pl-16 pr-16">
            <span className="ff-exo2 fw-500 f22 line-28 mb2 c238787">
              Asset Type
            </span>
            <div className="pr-10">
              <Button
                label="Close"
                icon="CANCEL"
                onClick={() => publish("MITIGATOR_HIDE_NEW_CONTENT")}
              />
            </div>
          </Row>

          <Col className="pl-30 pr-26">
            {/* VIEW MODE FORM */}
            <NMForm
              className="w-100"
              ref={viewRef}
              readOnly={true}
              fields={getAssetTypeFields(true, false)}
              formData={{
                id: currentAssetType.asset_type_id,
                type: currentAssetType.asset_type,
              }}
            />
          </Col>
          <Row
            className="pr-36 mt-16 w-100"
            justify="end"
            align="center"
            style={{ gap: "12px" }}
          >
            <Button
              label="Edit"
              icon="EDIT"
              onClick={() => setIsEditMode(true)}
            />
          </Row>
        </Col>
      ) : (
        <>
          <Col className="w-100 h-100 overflow-hide mt-36 pb-16">
            <Row justify="between" align="center" className="w-100 pl-16 pr-16">
              <span className="ff-exo2 fw-500 f22 line-28 mb2 c238787">
                Asset Type
              </span>
            </Row>
            <NMForm
              className="w-100 pl-30 pr-26"
              ref={_ref}
              // formData={{ id: "" }}
              formData={
                isEditMode
                  ? {
                    id: currentAssetType?.asset_type_id ?? "",
                    type: currentAssetType?.asset_type ?? "",
                  }
                  : {
                    id: "",
                    type: "",
                  }
              }
              id="new-asset-type-form"
              fields={
                isEditMode
                  ? getAssetTypeFields(false, true)
                  : getAssetTypeFields(false, false)
              }
            />
            <Row
              className="pr-26 mt-16 w-100"
              justify="end"
              align="center"
              style={{ gap: "12px" }}
            >
              {" "}
              <OutlinedButton
                label="Cancel"
                icon="CANCEL"
                onClick={handleCancel}
                loading={submitting}
              />
              <Button
                label="Save"
                icon="SAVE"
                onClick={handleSave}
                loading={submitting}
              />
            </Row>
          </Col>
        </>
      )}
    </div>
  );
};

export const CreateNewAsset = () => {
  const dispatch = useDispatch();
  const publish = usePub();
  const [submitting, setSubmitting] = React.useState(false);
  const _ref = React.useRef(null);
  const viewRef = React.useRef(null);
  const assetTypesList = useSelector(getAssetTypesList);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const assetId = params.get("id");
  const currentAsset = useSelector(getCurrentAsset);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    dispatch(mitigatorActions.fetchAssetTypeList());
  }, []);

  useEffect(() => {
    if (currentAsset && viewRef.current) {
      viewRef.current.updateFormData({
        asset_id: currentAsset?.asset_id ?? "",
        asset_type: currentAsset?.asset_type?.label ?? "",
        asset: currentAsset?.asset ?? "",
      });
    }
  }, [currentAsset]);

  useEffect(() => {
    if (!assetId) return;
    dispatch(mitigatorActions.fetchAsset({ asset_id: assetId }));
  }, [assetId]);

  useEffect(() => {
    if (isEditMode && currentAsset && _ref.current) {
      _ref.current.updateFormData({
        asset_id: currentAsset.asset_id,
        asset_type: currentAsset.asset_type.id, // THIS MATTERS
        asset: currentAsset.asset,
      });
    }
  }, [isEditMode, currentAsset]);

  const getOptions = (attribute) => {
    let options = [];
    switch (attribute) {
      case "asset_type":
        options = assetTypesList?.map((typ) => {
          return {
            id: typ.id,
            label: typ.asset_type,
          };
        });
        break;
      default:
        options = [];
        break;
    }
    return options;
  };
  const onComplete = () => {
    setSubmitting(false);
    setIsEditMode(false);
    publish("MITIGATOR_HIDE_NEW_CONTENT");
  };
  const onFailure = () => {
    setSubmitting(false);
  };
  const handleSave = () => {
    if (!_ref.current.validate()) {
      return;
    }
    const values = _ref.current.getValues();
    const body = {
      asset: values.asset,
      asset_type_id: values.asset_type,
    };
    const action = isEditMode
      ? mitigatorActions.updateAsset({
        id: assetId,
        ...body,
      })
      : mitigatorActions.createAsset(body);
    action.onComplete = onComplete;
    action.onFailure = onFailure;
    setSubmitting(true);
    dispatch(action);
  };
  const handleCancel = () => {
    if (submitting) return;
    _ref.current?.reset?.();
    publish("MITIGATOR_HIDE_NEW_CONTENT"); //hides create panel
  };
  return (
    <div
      style={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        padding: "0 12px",
        boxSizing: "border-box",
      }}
    >
      {assetId && currentAsset && !isEditMode ? (
        <Col className="w-100 h-100 overflow-hide mt-36 pb-16">
          <Row justify="between" align="" className="w-100 pl-16 pr-16">
            <span className="ff-exo2 fw-500 f22 line-28 mb2 c238787">
              Asset
            </span>
            <div className="pr-10">
              <Button
                label="Close"
                icon="CANCEL"
                onClick={() => publish("MITIGATOR_HIDE_NEW_CONTENT")}
              />
            </div>
          </Row>

          <Col className="pl-30 pr-26">
            {/* VIEW MODE FORM */}
            <NMForm
              className="w-100"
              ref={viewRef}
              readOnly={true}
              fields={getAssetFields(true, false)}
              formData={{
                asset_id: currentAsset?.asset_id ?? "",
                asset_type: currentAsset.asset_type?.label ?? "",
                asset: currentAsset?.asset ?? "",
              }}
            />
          </Col>
          <Row
            className="pr-36 mt-16 w-100"
            justify="end"
            align="center"
            style={{ gap: "12px" }}
          >
            <Button
              label="Edit"
              icon="EDIT"
              onClick={() => setIsEditMode(true)}
            />
          </Row>
        </Col>
      ) : (
        <>
          <Col className="w-100 h-100 overflow-hide mt-36 pb-16">
            <Row justify="between" align="center" className="w-100 pl-16 pr-16">
              <span className="ff-exo2 fw-500 f22 line-28 mb2 c238787">
                Asset
              </span>
            </Row>
            <NMForm
              className="w-100 pl-30 pr-26"
              ref={_ref}
              // formData={{ id: "" }}
              formData={{}}
              id="new-asset-form"
              fields={
                isEditMode
                  ? getAssetFields(false, true)
                  : getAssetFields(false, false)
              }
              getOptions={getOptions}
            />
            <Row
              className="pr-26 mt-16 w-100"
              justify="end"
              align="center"
              style={{ gap: "12px" }}
            >
              {" "}
              <OutlinedButton
                label="Cancel"
                icon="CANCEL"
                onClick={handleCancel}
                disabled={submitting}
              />
              <Button
                label="Save"
                icon="SAVE"
                onClick={handleSave}
                loading={submitting}
              />
            </Row>
          </Col>
        </>
      )}
    </div>
  );
};
